/**
 * SharedDocsTab – host shell for the Documents MFE.
 * Loads the remote docs script, mounts/unmounts the remote Documents tab.
 * Env: REACT_APP_SHARED_DOCS_TAB_URL – URL of the docs MFE bundle (e.g. http://localhost:8081/bundle.js).
 */
import { Box, Button, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import NMEFSpinner from "../../components/Spinner";
import useStyles from "./UseStyles";

const MFE_URL = process.env.REACT_APP_SHARED_DOCS_TAB_URL;

// Module-level script load state (deduplication across shell instances)
let scriptLoadPromise = null;
let scriptLoadedGlobal = false;
let scriptErrorGlobal = false;
let containerIdCounter = 0;

function loadScript(url) {
	if (scriptLoadedGlobal) return Promise.resolve();
	if (scriptLoadPromise) return scriptLoadPromise;
	scriptLoadPromise = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = url;
		script.async = true;
		script.onload = () => {
			scriptLoadedGlobal = true;
			scriptErrorGlobal = false;
			resolve();
		};
		script.onerror = (err) => {
			scriptErrorGlobal = true;
			console.error("[SharedDocsTab] Script load failed:", url, err);
			reject(new Error("Script load failed"));
		};
		document.body.appendChild(script);
	});
	return scriptLoadPromise;
}

function resetLoadState() {
	scriptLoadPromise = null;
	scriptErrorGlobal = false;
	const url = MFE_URL;
	if (url) {
		const existing = document.querySelector(`script[src="${url}"]`);
		if (existing && existing.parentNode)
			existing.parentNode.removeChild(existing);
	}
}

function unmountMfe(containerId) {
	const unmount = window.unmountDocsTab;
	if (typeof unmount === "function" && containerId) {
		try {
			unmount(containerId);
		} catch (e) {
			console.error("[SharedDocsTab] unmountDocsTab failed:", e);
		}
	}
}

const SharedDocsTab = forwardRef(function SharedDocsTab(props, ref) {
	const classes = useStyles();
	const {
		app,
		isReadOnly,
		appType,
		onClose,
		anchorEl,
		setCanCloseNotes,
		onReady,
	} = props;
	const containerIdRef = useRef(null);
	const mountedRef = useRef(false);
	const propsRef = useRef({
		app,
		isReadOnly,
		appType,
		onClose,
		anchorEl,
		setCanCloseNotes,
	});
	propsRef.current = {
		app,
		isReadOnly,
		appType,
		onClose,
		anchorEl,
		setCanCloseNotes,
	};
	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;
	if (!containerIdRef.current) {
		containerIdRef.current = `shared-docs-mfe-${++containerIdCounter}-${Date.now()}`;
	}
	const containerId = containerIdRef.current;
	const [mfeMounted, setMfeMounted] = useState(false);

	useImperativeHandle(
		ref,
		() => ({
			unmount: () => {
				if (mountedRef.current) {
					unmountMfe(containerId);
					mountedRef.current = false;
				}
			},
		}),
		[containerId]
	);

	const [loaded, setLoaded] = useState(scriptLoadedGlobal);
	const [error, setError] = useState(!MFE_URL || scriptErrorGlobal);
	const [retryKey, setRetryKey] = useState(0);

	useEffect(() => {
		if (!MFE_URL) {
			setError(true);
			console.warn(
				"[SharedDocsTab] REACT_APP_SHARED_DOCS_TAB_URL is not configured."
			);
			return;
		}
		if (scriptLoadedGlobal) {
			setLoaded(true);
			setError(false);
			if (onReadyRef.current) onReadyRef.current();
			return;
		}
		if (scriptErrorGlobal) {
			setError(true);
			return;
		}

		let cancelled = false;
		loadScript(MFE_URL)
			.then(() => {
				if (!cancelled) {
					setLoaded(true);
					setError(false);
					if (onReadyRef.current) onReadyRef.current();
				}
			})
			.catch((err) => {
				if (!cancelled) {
					setError(true);
					console.error("[SharedDocsTab] Load error:", err);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [retryKey]);

	useEffect(() => {
		if (!loaded || error || !containerId) return;
		const mount = window.mountDocsTab;
		if (typeof mount !== "function") {
			console.error("[SharedDocsTab] window.mountDocsTab is not a function");
			setError(true);
			return;
		}
		const el = document.getElementById(containerId);
		if (!el) {
			console.error("[SharedDocsTab] Container not found:", containerId);
			return;
		}
		const {
			app: a,
			isReadOnly: ro,
			appType: at,
			onClose: oc,
			anchorEl: ae,
			setCanCloseNotes: scc,
		} = propsRef.current;
		try {
			mount(containerId, {
				app: a,
				isReadOnly: ro,
				appType: at || null,
				onClose: oc,
				anchorEl: ae,
				setCanCloseNotes: scc,
			});
			mountedRef.current = true;
			setMfeMounted(true);
		} catch (err) {
			console.error("[SharedDocsTab] mountDocsTab failed:", err);
			setError(true);
		}
		return () => {
			mountedRef.current = false;
			setMfeMounted(false);
			unmountMfe(containerId);
		};
	}, [loaded, error, containerId]);

	const handleRetry = () => {
		resetLoadState();
		setLoaded(false);
		setError(false);
		setRetryKey((k) => k + 1);
	};

	if (!MFE_URL || error) {
		return (
			<div className={classes.root}>
				<Box component="div" className={classes.errorBox}>
					<Typography color="textSecondary">
						Documents are temporarily unavailable.
					</Typography>
					{MFE_URL && (
						<Button
							variant="outlined"
							size="small"
							className={classes.retryButton}
							onClick={handleRetry}>
							Retry
						</Button>
					)}
				</Box>
			</div>
		);
	}

	if (!loaded) {
		return (
			<div className={classes.loadingRoot} data-shared-docs-mfe-loading>
				<NMEFSpinner />
			</div>
		);
	}

	return (
		<div className={classes.root}>
			{!mfeMounted && (
				<Box
					position="absolute"
					top={0}
					left={0}
					right={0}
					bottom={0}
					display="flex"
					justifyContent="center"
					alignItems="center"
					bgcolor="background.paper">
					<NMEFSpinner />
				</Box>
			)}
			<div
				id={containerId}
				data-shared-docs-mfe
				data-app-id={app?.id}
				className={classes.container}
				style={{
					visibility: mfeMounted ? "visible" : "hidden",
					minHeight: mfeMounted ? undefined : 200,
				}}
			/>
		</div>
	);
});

SharedDocsTab.propTypes = {
	app: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		broker_id: PropTypes.string,
		contract_number: PropTypes.string,
		locked_by_email: PropTypes.string,
	}),
	isReadOnly: PropTypes.bool,
	appType: PropTypes.string,
	onClose: PropTypes.func,
	anchorEl: PropTypes.object,
	setCanCloseNotes: PropTypes.func,
	onReady: PropTypes.func,
};

SharedDocsTab.defaultProps = {
	app: null,
	isReadOnly: false,
	appType: null,
	onClose: undefined,
	anchorEl: undefined,
	setCanCloseNotes: undefined,
	onReady: undefined,
};

export default SharedDocsTab;
